import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ChevronRight, ChevronLeft, Check, Scan, Upload, Image, Camera } from 'lucide-react';
import IDScannerModal from './IDScannerModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MultiStepReservationModal = ({ open, onClose, initialData, onSubmit, property }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [showIdCamera, setShowIdCamera] = useState(false);
  const idVideoRef = useRef(null);
  const idStreamRef = useRef(null);
  const [autoCalcTax, setAutoCalcTax] = useState(true);
  const [formData, setFormData] = useState({
    // Step 1: Stay & Rate
    check_in: initialData?.check_in || '',
    check_out: initialData?.check_out || '',
    unit_id: initialData?.unit_id || '',
    adults: 1,
    children: 0,
    base_amount: '',  // No prefilled amount
    tax_amount: 0,
    stay_type: 'overnight',
    
    // Step 2: Guest
    guest_id: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_dob: '',
    guest_address: '',
    guest_city: '',
    guest_state: '',
    guest_zip: '',
    document_type: '',
    document_number: '',
    guest_note: '',
    
    // Step 3: Payment
    payment_method: 'Cash',
    payment_amount: 0,
    notes: ''
  });

  // Use local date, not UTC — prevents blocking today's date in evening hours
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  // Auto-calculate checkout when stay type or check-in changes
  const calcCheckout = (checkIn, stayType) => {
    if (!checkIn) return '';
    const ci = new Date(checkIn + 'T12:00:00'); // noon to avoid DST issues
    const stayDays = { 'overnight': 1, '1hr': 0, '2hr': 0, '6hr': 0, '12hr': 0, 'day_use': 1, 'weekly': 7 };
    const days = stayDays[stayType] ?? 1;
    if (days === 0) return checkIn; // hourly stays: same-day checkout
    ci.setDate(ci.getDate() + days);
    return `${ci.getFullYear()}-${String(ci.getMonth()+1).padStart(2,'0')}-${String(ci.getDate()).padStart(2,'0')}`;
  };

  useEffect(() => {
    if (!autoCalcTax) return;
    if (property && formData.base_amount > 0) {
      const totalTaxRate = parseFloat(property.tax_total_rate) || 0;
      const calculatedTax = (formData.base_amount * totalTaxRate) / 100;
      
      setFormData(prev => ({
        ...prev,
        tax_amount: parseFloat(calculatedTax.toFixed(2))
      }));
    } else if (formData.base_amount <= 0 || !formData.base_amount) {
      // Reset tax if no base amount
      setFormData(prev => ({
        ...prev,
        tax_amount: 0
      }));
    }
  }, [formData.base_amount, property, autoCalcTax]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      // Reset to step 1 and clear form data
      setCurrentStep(1);
      setShowScannerModal(false);
      setFormData({
        check_in: initialData?.check_in || '',
        check_out: initialData?.check_out || '',
        unit_id: initialData?.unit_id || '',
        adults: 1,
        children: 0,
        base_amount: '',
        tax_amount: 0,
        stay_type: 'overnight',
        guest_id: '',
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guest_dob: '',
        guest_address: '',
        guest_city: '',
        guest_state: '',
        guest_zip: '',
        document_type: '',
        document_number: '',
        guest_note: '',
        payment_method: 'Cash',
        payment_amount: 0,
        notes: ''
      });
      setIdImageUrl(null);
      setAutoCalcTax(true);
      stopIdCamera();
    }
  }, [open, initialData]);

  // Cleanup ID camera on unmount or close
  useEffect(() => {
    return () => {
      if (idStreamRef.current) {
        idStreamRef.current.getTracks().forEach(t => t.stop());
        idStreamRef.current = null;
      }
    };
  }, []);

  const startIdCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      idStreamRef.current = stream;
      setShowIdCamera(true);
      setTimeout(() => {
        if (idVideoRef.current) {
          idVideoRef.current.srcObject = stream;
          idVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (err) {
      toast.error('Camera access denied');
    }
  };

  const captureIdPhoto = () => {
    if (!idVideoRef.current) return;
    const vw = idVideoRef.current.videoWidth;
    const vh = idVideoRef.current.videoHeight;
    if (!vw || !vh) { toast.error('Camera not ready. Please wait and try again.'); return; }
    const canvas = document.createElement('canvas');
    canvas.width = vw;
    canvas.height = vh;
    canvas.getContext('2d').drawImage(idVideoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob || blob.size < 500) { toast.error('Photo capture failed. Please try again.'); return; }
      const file = new File([blob], 'id-photo.jpg', { type: 'image/jpeg' });
      setFormData(prev => ({...prev, _idImageFile: file}));
      setIdImageUrl(URL.createObjectURL(blob));
      toast.success('ID photo captured');
      stopIdCamera();
    }, 'image/jpeg', 0.9);
  };

  const stopIdCamera = () => {
    if (idStreamRef.current) {
      idStreamRef.current.getTracks().forEach(t => t.stop());
      idStreamRef.current = null;
    }
    setShowIdCamera(false);
  };

  const steps = [
    { number: 1, title: 'Stay & Rate', desc: 'Dates and pricing' },
    { number: 2, title: 'Guest Details', desc: 'Contact and ID' },
    { number: 3, title: 'Payment', desc: 'Payment method' }
  ];

  const nextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      if (!formData.check_in || !formData.check_out) {
        alert('Please fill in check-in and check-out dates.');
        return;
      }
      if (!formData.base_amount || formData.base_amount <= 0) {
        alert('Please enter a valid room rate.');
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.guest_name || !formData.guest_name.trim()) {
        alert('Please enter the guest name.');
        return;
      }
      if (!formData.guest_phone || !formData.guest_phone.trim()) {
        alert('Please enter the guest phone number.');
        return;
      }
    }
    if (currentStep < 3) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const handleScannedData = (scannedData) => {
    if (!scannedData) return;
    
    // Auto-fill form with scanned data
    const updates = {};
    
    if (scannedData.fullName) {
      updates.guest_name = scannedData.fullName;
    } else if (scannedData.firstName || scannedData.lastName) {
      updates.guest_name = [scannedData.firstName, scannedData.lastName].filter(Boolean).join(' ');
    }
    
    if (scannedData.documentNumber) updates.document_number = scannedData.documentNumber;
    if (scannedData.documentType) updates.document_type = scannedData.documentType;
    if (scannedData.dateOfBirth) updates.guest_dob = scannedData.dateOfBirth;
    if (scannedData.addressLine1) {
      updates.guest_address = scannedData.addressLine1;
    }
    if (scannedData.city) updates.guest_city = scannedData.city;
    if (scannedData.state) updates.guest_state = scannedData.state;
    if (scannedData.postalCode) updates.guest_zip = scannedData.postalCode;
    
    setFormData(prev => ({ ...prev, ...updates }));
    setShowScannerModal(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Reservation</DialogTitle>
        </DialogHeader>

        {/* Progress Indicator - Fixed alignment */}
        <div className="flex items-start justify-between mb-6">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep >= step.number 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {currentStep > step.number ? <Check size={20} /> : step.number}
                </div>
                <p className="text-xs mt-1 font-medium text-center">{step.title}</p>
                <p className="text-xs text-slate-500 text-center">{step.desc}</p>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-1 mx-2 mt-5 flex-1 ${
                  currentStep > step.number ? 'bg-cyan-600' : 'bg-slate-200'
                }`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Stay & Rate */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Stay & Rate Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stay Type *</Label>
                  <select
                    value={formData.stay_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      const newCheckout = calcCheckout(formData.check_in, newType);
                      setFormData({...formData, stay_type: newType, check_out: newCheckout || formData.check_out});
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="stay-type-select"
                  >
                    <option value="overnight">Overnight</option>
                    <option value="1hr">1 Hour</option>
                    <option value="2hr">2 Hours</option>
                    <option value="6hr">6 Hours</option>
                    <option value="12hr">12 Hours</option>
                    <option value="day_use">Full Day</option>
                    <option value="weekly">Full Week</option>
                  </select>
                </div>
                <div></div>
                <div>
                  <Label>Check-In Date *</Label>
                  <Input
                    type="date"
                    min={today}
                    value={formData.check_in}
                    onChange={(e) => {
                      const newCheckIn = e.target.value;
                      const newCheckout = calcCheckout(newCheckIn, formData.stay_type);
                      setFormData({...formData, check_in: newCheckIn, check_out: newCheckout || formData.check_out});
                    }}
                    required
                    data-testid="checkin-date-input"
                  />
                </div>
                <div>
                  <Label>Check-Out Date *</Label>
                  <Input
                    type="date"
                    min={formData.check_in || today}
                    value={formData.check_out}
                    onChange={(e) => setFormData({...formData, check_out: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Adults *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.adults}
                    onChange={(e) => setFormData({...formData, adults: e.target.value === '' ? '' : parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div>
                  <Label>Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.children}
                    onChange={(e) => setFormData({...formData, children: e.target.value === '' ? '' : parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Room Rate ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.base_amount}
                    onChange={(e) => setFormData({...formData, base_amount: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    required
                  />
                </div>
                <div>
                  <Label>Tax ($) {autoCalcTax && property ? <span className="text-xs text-slate-500">(Auto-calculated)</span> : null}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.tax_amount}
                    readOnly={autoCalcTax && !!property}
                    onChange={(e) => setFormData({...formData, tax_amount: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    className={autoCalcTax && property ? 'bg-slate-100' : ''}
                  />
                  <label className="flex items-center gap-2 mt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!autoCalcTax}
                      onChange={(e) => setAutoCalcTax(!e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-xs text-slate-500">Enter tax manually (disable auto-calc)</span>
                  </label>
                  {autoCalcTax && property && property.tax_total_rate > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Total Tax Rate: {property.tax_total_rate}%
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded">
                <p className="text-sm font-medium">Total: ${((parseFloat(formData.base_amount) || 0) + (parseFloat(formData.tax_amount) || 0)).toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Step 2: Guest Details */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Guest Details</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowScannerModal(true)}
                  className="gap-2"
                >
                  <Scan size={16} />
                  Scan ID
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.guest_name}
                    onChange={(e) => setFormData({...formData, guest_name: e.target.value})}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.guest_email}
                    onChange={(e) => setFormData({...formData, guest_email: e.target.value})}
                    placeholder="guest@email.com"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    value={formData.guest_phone}
                    onChange={(e) => setFormData({...formData, guest_phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                    required
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.guest_dob}
                    onChange={(e) => setFormData({...formData, guest_dob: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Document Type</Label>
                  <select
                    value={formData.document_type}
                    onChange={(e) => setFormData({...formData, document_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select...</option>
                    <option value="DRIVERS_LICENSE">Driver's License</option>
                    <option value="PASSPORT">Passport</option>
                    <option value="NATIONAL_ID">National ID</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Street Address</Label>
                  <Input
                    data-testid="guest-address-input"
                    value={formData.guest_address}
                    onChange={(e) => setFormData({...formData, guest_address: e.target.value})}
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    data-testid="guest-city-input"
                    value={formData.guest_city}
                    onChange={(e) => setFormData({...formData, guest_city: e.target.value})}
                    placeholder="Miami"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    data-testid="guest-state-input"
                    value={formData.guest_state}
                    onChange={(e) => setFormData({...formData, guest_state: e.target.value})}
                    placeholder="FL"
                  />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input
                    data-testid="guest-zip-input"
                    value={formData.guest_zip}
                    onChange={(e) => setFormData({...formData, guest_zip: e.target.value})}
                    placeholder="33101"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Document Number</Label>
                  <Input
                    value={formData.document_number}
                    onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                    placeholder="DL123456 or P1234567"
                  />
                </div>
                <div className="col-span-2">
                  <Label>ID Photo for Folio</Label>
                  {showIdCamera ? (
                    <div className="space-y-2">
                      <video ref={idVideoRef} className="w-full rounded-lg border-2 border-slate-300 bg-black" style={{ maxHeight: '250px' }} autoPlay playsInline />
                      <div className="flex gap-2">
                        <Button type="button" onClick={captureIdPhoto} className="flex-1 bg-green-600 hover:bg-green-700">
                          <Camera size={14} className="mr-1" /> Capture Photo
                        </Button>
                        <Button type="button" variant="outline" onClick={stopIdCamera} className="flex-1">Cancel</Button>
                      </div>
                    </div>
                  ) : idImageUrl ? (
                    <div className="flex items-center gap-3 p-3 border-2 border-green-400 bg-green-50 rounded-lg">
                      <img src={idImageUrl} alt="ID" className="max-h-24 rounded border" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-700">ID photo attached</p>
                        <div className="flex gap-2 mt-2">
                          <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border rounded-md cursor-pointer hover:bg-slate-50 bg-white">
                            <Camera size={12} /> Retake
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setFormData(prev => ({...prev, _idImageFile: file}));
                              setIdImageUrl(URL.createObjectURL(file));
                            }} />
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-1 px-3 py-2 text-sm border-2 border-cyan-300 rounded-md cursor-pointer hover:bg-cyan-50 bg-white font-medium text-cyan-700">
                          <Camera size={16} /> Take Photo of ID
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size < 500) { toast.error('Photo appears empty. Please retake.'); return; }
                            setFormData(prev => ({...prev, _idImageFile: file}));
                            setIdImageUrl(URL.createObjectURL(file));
                            toast.success('ID photo attached');
                          }} />
                        </label>
                        <label className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded-md cursor-pointer hover:bg-slate-50">
                          <Upload size={14} /> Upload File
                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setFormData(prev => ({...prev, _idImageFile: file}));
                            setIdImageUrl(URL.createObjectURL(file));
                            toast.success('ID image attached');
                          }} />
                        </label>
                        <Button type="button" variant="outline" size="sm" onClick={startIdCamera} className="gap-1">
                          <Camera size={14} /> Live Camera
                        </Button>
                        {property?.scanner_ip && (
                          <Button type="button" variant="outline" size="sm" onClick={() => {
                            window.open(`http://${property.scanner_ip}`, '_blank', 'width=900,height=700');
                          }} className="gap-1">
                            <Scan size={14} /> Open Scanner
                          </Button>
                        )}
                      </div>
                      <div
                        className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:border-cyan-500 hover:bg-cyan-50 transition-all"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-cyan-500', 'bg-cyan-50'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-cyan-500', 'bg-cyan-50'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-cyan-500', 'bg-cyan-50');
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            setFormData(prev => ({...prev, _idImageFile: file}));
                            setIdImageUrl(URL.createObjectURL(file));
                            toast.success('ID image attached');
                          }
                        }}
                        onPaste={(e) => {
                          const items = e.clipboardData?.items;
                          if (!items) return;
                          for (const item of items) {
                            if (item.type.startsWith('image/')) {
                              const file = item.getAsFile();
                              setFormData(prev => ({...prev, _idImageFile: file}));
                              setIdImageUrl(URL.createObjectURL(file));
                              toast.success('ID image pasted');
                              break;
                            }
                          }
                        }}
                        tabIndex={0}
                      >
                        <p className="text-xs text-slate-500">Or drag & drop / Ctrl+V to paste</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Internal Guest Notes (optional)</Label>
                  <textarea
                    value={formData.guest_note}
                    onChange={(e) => setFormData({...formData, guest_note: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                    placeholder="e.g., Prefers high floor, VIP client, quiet room requested..."
                  />
                  <p className="text-xs text-slate-500 mt-1">Only visible to staff</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Payment & Final Notes</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Method *</Label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Direct Billing">Direct Billing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Payment Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({...formData, payment_amount: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Additional Notes</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                    placeholder="Special requests, booking notes..."
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold">Reservation Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Guest: {formData.guest_name}</div>
                  <div>Phone: {formData.guest_phone}</div>
                  <div>Check-in: {formData.check_in}</div>
                  <div>Check-out: {formData.check_out}</div>
                  <div>Guests: {formData.adults} adults, {formData.children} children</div>
                  <div>Rate: ${formData.base_amount}/night</div>
                  <div>Tax: ${formData.tax_amount}</div>
                  <div className="font-semibold">Total: ${(formData.base_amount + formData.tax_amount).toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </Button>

          <div className="text-sm text-slate-500">
            Step {currentStep} of 3
          </div>

          {currentStep < 3 ? (
            <Button onClick={nextStep}>
              Next
              <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">
                <Check size={16} className="mr-1" />
                {formData.check_in === today
                  ? 'Reserve & Check In'
                  : 'Create Reservation'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
      
      {/* ID Scanner Modal */}
      <IDScannerModal
        open={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onDataScanned={handleScannedData}
      />
    </Dialog>
  );
};

export default MultiStepReservationModal;
